import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { PageContainer } from '../../../common/components/PageContainer';
import { AdBudgets, AdPacing, AdProvider, useAdsApi } from '../../../common/hooks/use-ads-api.hook';
import { FeatureGate } from '../../../components/FeatureGate';
import { ConnectFirst, PageHeading, ProviderMark, formatMoney, readApiError, useAdConnections } from './ads-shared';

const BudgetsBody = () => {
    const { t } = useTranslation();
    const api = useAdsApi();
    const connections = useAdConnections();
    const [budgets, setBudgets] = useState<AdBudgets | null>(null);
    const [pacing, setPacing] = useState<AdPacing | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
    const [provider, setProvider] = useState<'' | AdProvider>('');
    const [amount, setAmount] = useState('');

    const currency = connections.data?.connections.find((c) => c.currency)?.currency ?? null;

    const load = async () => {
        setLoading(true);
        try {
            const today = dayjs().format('YYYY-MM-DD');
            const [b, overview] = await Promise.all([
                api.getBudgets(),
                api.getOverview({ startDate: today, endDate: today }),
            ]);
            setBudgets(b);
            setPacing(overview.pacing);
        } catch (error) {
            console.error('Error loading ad budgets:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (connections.hasConnection) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connections.hasConnection]);

    // Pre-fill with what is already set, so editing a budget does not start from blank.
    useEffect(() => {
        const existing = budgets?.budgets.find((b) => b.month === month && (b.provider ?? '') === provider);
        setAmount(existing ? String(existing.amount) : '');
    }, [budgets, month, provider]);

    const save = async () => {
        const value = Number(amount);
        if (!Number.isFinite(value) || value < 0) return;
        setSaving(true);
        try {
            const result = await api.setBudget({ month, provider: provider || undefined, amount: value, currency: currency ?? undefined });
            setBudgets(result);
            toast.success(t('ADS.BUDGETS.SAVED', 'Budget saved.'));
            load();
        } catch (error) {
            toast.error(await readApiError(error, t('ADS.BUDGETS.SAVE_FAILED', 'The budget could not be saved.')));
        } finally {
            setSaving(false);
        }
    };

    const heading = (
        <PageHeading
            title={t('ADS.BUDGETS.TITLE', 'Budgets')}
            subtitle={t('ADS.BUDGETS.SUBTITLE', 'What you mean to spend each month across Meta and Google. The platforms only know daily budgets per campaign; this is how you see the month.')}
        />
    );

    if (connections.loading) return <div className="flex flex-col gap-6">{heading}<Skeleton className="h-64 w-full" /></div>;
    if (!connections.hasConnection) return <div className="flex flex-col gap-6">{heading}<ConnectFirst /></div>;

    const months = Array.from({ length: 4 }, (_, i) => dayjs().add(i - 1, 'month').format('YYYY-MM'));
    const pct = pacing?.budget ? Math.min(100, Math.round((pacing.spend / pacing.budget) * 100)) : 0;

    return (
        <div className="flex flex-col gap-6">
            {heading}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>{t('ADS.PACING.TITLE', 'This month')}</CardTitle>
                        <CardDescription>{dayjs().format('MMMM YYYY')}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4">
                        {loading ? <Skeleton className="h-24 w-full" /> : pacing?.budget ? (
                            <>
                                <div className="flex items-baseline justify-between">
                                    <span className="font-heading text-3xl font-medium tabular-nums">{formatMoney(pacing.spend, pacing.currency ?? currency)}</span>
                                    <span className="text-sm text-muted-foreground">
                                        {t('ADS.BUDGETS.OF', 'of {{budget}}', { budget: formatMoney(pacing.budget, pacing.currency ?? currency) })}
                                    </span>
                                </div>
                                <Progress value={pct} />
                                <dl className="grid grid-cols-3 gap-2 text-sm">
                                    <div>
                                        <dt className="text-xs text-muted-foreground">{t('ADS.BUDGETS.EXPECTED', 'Expected by today')}</dt>
                                        <dd className="tabular-nums">{formatMoney(pacing.expected, pacing.currency ?? currency)}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-xs text-muted-foreground">{t('ADS.BUDGETS.PROJECTED', 'Month at this rate')}</dt>
                                        <dd className="tabular-nums">{formatMoney(pacing.projected, pacing.currency ?? currency)}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-xs text-muted-foreground">{t('ADS.BUDGETS.REMAINING', 'Left to spend')}</dt>
                                        <dd className="tabular-nums">{formatMoney(pacing.remaining, pacing.currency ?? currency)}</dd>
                                    </div>
                                </dl>
                            </>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                {t('ADS.BUDGETS.NONE_THIS_MONTH', '{{spend}} spent so far this month, and no budget to compare it with. Set one here.', {
                                    spend: formatMoney(pacing?.spend ?? 0, currency),
                                })}
                            </p>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('ADS.BUDGETS.SET_TITLE', 'Set a monthly budget')}</CardTitle>
                        <CardDescription>{t('ADS.BUDGETS.SET_DESC', 'For both platforms together, or one at a time. It does not change anything in your ad accounts.')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <FieldGroup>
                            <div className="grid grid-cols-2 gap-4">
                                <Field>
                                    <FieldLabel htmlFor="budget-month">{t('ADS.BUDGETS.MONTH', 'Month')}</FieldLabel>
                                    <NativeSelect id="budget-month" value={month} onChange={(e) => setMonth(e.target.value)}>
                                        {months.map((m) => (
                                            <NativeSelectOption key={m} value={m}>{dayjs(`${m}-01`).format('MMMM YYYY')}</NativeSelectOption>
                                        ))}
                                    </NativeSelect>
                                </Field>
                                <Field>
                                    <FieldLabel htmlFor="budget-provider">{t('ADS.BUDGETS.PLATFORM', 'Platform')}</FieldLabel>
                                    <NativeSelect id="budget-provider" value={provider} onChange={(e) => setProvider(e.target.value as '' | AdProvider)}>
                                        <NativeSelectOption value="">{t('ADS.BUDGETS.ALL_PLATFORMS', 'All platforms')}</NativeSelectOption>
                                        <NativeSelectOption value="meta">Meta</NativeSelectOption>
                                        <NativeSelectOption value="google">Google</NativeSelectOption>
                                    </NativeSelect>
                                </Field>
                            </div>
                            <Field>
                                <FieldLabel htmlFor="budget-amount">{t('ADS.BUDGETS.AMOUNT', 'Amount')} {currency ? `(${currency})` : ''}</FieldLabel>
                                <Input id="budget-amount" type="number" min="0" step="10" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
                                {Number(amount) > 0 && (
                                    <FieldDescription>
                                        {t('ADS.BUDGETS.PER_DAY', 'About {{amount}} a day.', { amount: formatMoney(Number(amount) / 30, currency) })}
                                    </FieldDescription>
                                )}
                            </Field>
                            <Button variant="outline" onClick={save} disabled={saving || amount === '' || Number(amount) < 0} className="w-fit">
                                {saving && <Spinner data-icon="inline-start" />}
                                {t('ADS.COMMON.SAVE', 'Save')}
                            </Button>
                        </FieldGroup>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>{t('ADS.BUDGETS.HISTORY', 'Budget and spend by month')}</CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? <Skeleton className="h-32 w-full" /> : budgets?.budgets.length ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('ADS.BUDGETS.MONTH', 'Month')}</TableHead>
                                    <TableHead>{t('ADS.BUDGETS.PLATFORM', 'Platform')}</TableHead>
                                    <TableHead className="text-end">{t('ADS.BUDGETS.BUDGET', 'Budget')}</TableHead>
                                    <TableHead className="text-end">{t('ADS.METRIC.SPEND', 'Spend')}</TableHead>
                                    <TableHead className="text-end">{t('ADS.BUDGETS.USED', 'Used')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {budgets.budgets.map((b) => (
                                    <TableRow key={`${b.month}:${b.provider ?? 'all'}`}>
                                        <TableCell>{dayjs(`${b.month}-01`).format('MMMM YYYY')}</TableCell>
                                        <TableCell>{b.provider ? <ProviderMark provider={b.provider} withName /> : t('ADS.BUDGETS.ALL_PLATFORMS', 'All platforms')}</TableCell>
                                        <TableCell className="text-end tabular-nums">{formatMoney(b.amount, b.currency ?? currency)}</TableCell>
                                        <TableCell className="text-end tabular-nums">{formatMoney(b.spend, b.currency ?? currency)}</TableCell>
                                        <TableCell className="text-end tabular-nums">{b.amount ? `${Math.round((b.spend / b.amount) * 100)}%` : '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <p className="py-6 text-center text-sm text-muted-foreground">{t('ADS.BUDGETS.EMPTY', 'No budgets set yet.')}</p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export const AdsBudgetsPage = () => {
    const { t } = useTranslation();
    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.ADS.TITLE', 'Ads') }, { label: t('SIDEBAR.ADS.BUDGETS', 'Budgets') }]}>
            <FeatureGate feature="adsManager">
                <BudgetsBody />
            </FeatureGate>
        </PageContainer>
    );
};

export default AdsBudgetsPage;
